import React, { useState } from 'react'
import Box from '@material-ui/core/Box'
import Drawer from '@material-ui/core/Drawer'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import clsx from 'clsx'
import CheckIcon from 'components/material-symbol/icons/Check'
import CloseIcon from 'components/material-symbol/icons/Close'
import UnfoldMoreIcon from 'components/material-symbol/icons/UnfoldMore'

const FACILITIES_SECTION = {
  id: 'profile-section-facilities',
  label: 'Facilities',
  pageTitle: 'Where do you coach?',
  subtitle: 'Add the facilities where you offer lessons.',
  fields: ['facilities'],
}

const EDIT_WEB_ADDRESS_SECTION = {
  id: 'profile-section-web-address',
  label: 'Web address',
  pageTitle: 'Your profile URL',
  subtitle: 'Copy or open your public PGA.com link.',
  fields: ['customSlug'],
}

const EDIT_FACILITIES_SECTION = {
  id: 'profile-section-facilities',
  label: 'Facilities',
  pageTitle: 'Your facilities',
  subtitle: 'View the facilities shown on your profile.',
  fields: [],
}

const PROFILE_SECTIONS_BASE = [
  {
    id: 'profile-section-about',
    label: 'About you',
    pageTitle: 'Tell students about you',
    subtitle: 'Your name and profile photo.',
    fields: [
      'firstName',
      'lastName',
      'displayName',
      'gender',
      'title',
      'profilePhoto',
    ],
  },
  {
    id: 'profile-section-contact',
    label: 'Contact & location',
    pageTitle: 'How can students reach you?',
    subtitle: 'Contact details and teaching location.',
    fields: [
      'email',
      'phoneNumber',
      'phoneExt',
      'mobileNumber',
      'city',
      'state',
      'zipCode',
    ],
  },
  {
    id: 'profile-section-personal',
    label: 'Personal & media',
    pageTitle: 'Add your profile, coaching & social details',
    subtitle: 'Social links, testimonials, and photos.',
    fields: [
      'bio',
      'numberOfStudents',
      'numberOfFollowers',
      'socialJsonData',
      'testimonialsJsonData',
      'photosJsonData',
    ],
  },
  {
    id: 'profile-section-web-address',
    label: 'Web address',
    pageTitle: 'Choose your PGA.com profile URL',
    subtitle: 'This is the link students will use to find you.',
    fields: ['customSlug'],
  },
  {
    id: 'profile-section-experience',
    label: 'Experience',
    pageTitle: 'Your experience',
    subtitle: 'Select the experience types that apply to you.',
    fields: ['experiences'],
  },
  {
    id: 'profile-section-services',
    label: 'Services & skills',
    pageTitle: 'Services & skills',
    subtitle: 'Services, skills, and tools you use while coaching.',
    fields: ['prices', 'services', 'skills', 'toolJsonData'],
  },
]

const withEditPersonalFields = sections =>
  sections.map(section =>
    section.id === 'profile-section-personal'
      ? {
        ...section,
        fields: [...section.fields, 'introductionVideoUrl'],
      }
      : section,
  )

const withEditWebAddressSection = sections =>
  sections.map(section =>
    section.id === 'profile-section-web-address'
      ? EDIT_WEB_ADDRESS_SECTION
      : section,
  )

const withFacilitiesSection = (sections, { isEdit = false } = {}) => {
  const nextSections = [...sections]
  const webAddressIndex = nextSections.findIndex(
    section => section.id === 'profile-section-web-address',
  )
  nextSections.splice(
    webAddressIndex + 1,
    0,
    isEdit ? EDIT_FACILITIES_SECTION : FACILITIES_SECTION,
  )
  return nextSections
}

export const getProfileSections = ({
  includeFacilities = false,
  isEdit = false,
} = {}) => {
  let sections = isEdit
    ? withEditWebAddressSection(withEditPersonalFields(PROFILE_SECTIONS_BASE))
    : [...PROFILE_SECTIONS_BASE]

  if (includeFacilities || isEdit) {
    sections = withFacilitiesSection(sections, { isEdit })
  }

  return sections
}

export const getActiveSectionIndex = (sections, sectionId) =>
  Math.max(
    0,
    sections.findIndex(section => section.id === sectionId),
  )

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  desktopNav: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  mobileNav: {
    display: 'none',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
    },
  },
  mobileSummary: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    width: '100%',
    padding: theme.spacing(2, 2.5),
    border: '1px solid #E0E0E0',
    borderRadius: 12,
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    textAlign: 'left',
    fontFamily: 'inherit',
  },
  mobileSummaryText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    minWidth: 0,
  },
  mobileSummaryLabel: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#172024',
    lineHeight: 1.25,
  },
  mobileSummaryStep: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: '#757575',
    lineHeight: 1.25,
  },
  mobileSummaryIcon: {
    color: '#172024',
    flexShrink: 0,
  },
  drawerPaper: {
    padding: theme.spacing(2.5, 2, 4),
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '90vh',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  drawerTitle: {
    fontSize: '1.5rem',
    fontWeight: 400,
    color: '#172024',
    lineHeight: 1.3,
    margin: 0,
  },
  drawerCloseButton: {
    padding: theme.spacing(0.5),
    color: '#172024',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
  },
  stepTrack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 18,
    marginRight: theme.spacing(2),
    flexShrink: 0,
    paddingTop: 3,
  },
  circle: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    border: '2.5px solid #587C8C',
    backgroundColor: theme.palette.common.white,
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  circleCompleted: {
    borderColor: '#0053FF',
    backgroundColor: '#0053FF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkIcon: {
    color: theme.palette.common.white,
    fontSize: 12,
    width: 12,
    height: 12,
  },
  circleActive: {
    borderColor: '#0053FF',
    backgroundColor: '#E8EFFF',
  },
  connector: {
    width: 2,
    height: 36,
    flexShrink: 0,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    backgroundImage:
      'repeating-linear-gradient(to bottom, #587C8C 0, #587C8C 3px, transparent 3px, transparent 6px)',
    backgroundRepeat: 'repeat-y',
    backgroundPosition: 'center top',
  },
  connectorCompleted: {
    backgroundImage:
      'repeating-linear-gradient(to bottom, #0053FF 0, #0053FF 3px, transparent 3px, transparent 6px)',
  },
  stepButton: {
    display: 'block',
    flex: 1,
    minWidth: 0,
    padding: 0,
    paddingBottom: theme.spacing(3.5),
    border: 'none',
    backgroundColor: 'transparent',
    textAlign: 'left',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  stepButtonLast: {
    paddingBottom: 0,
  },
  stepLabel: {
    display: 'block',
    fontSize: '1rem',
    fontWeight: 400,
    color: '#172024',
    lineHeight: 1.25,
    marginBottom: theme.spacing(0.25),
  },
  stepLabelActive: {
    color: '#0053FF',
  },
  stepNumber: {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 400,
    color: '#757575',
    lineHeight: 1.25,
  },
}))

const SectionStepList = ({
  classes,
  sections,
  activeIndex,
  completedSectionIds,
  onSectionClick,
}) => (
  <>
    {sections.map((section, index) => {
      const isActive = index === activeIndex
      const isCompleted = completedSectionIds.has(section.id) && !isActive
      const isLast = index === sections.length - 1
      const stepNumber = index + 1
      const connectorCompleted = completedSectionIds.has(section.id)

      return (
        <Box key={section.id} className={classes.stepItem}>
          <Box className={classes.stepTrack} aria-hidden="true">
            <Box
              className={clsx(
                classes.circle,
                isCompleted && classes.circleCompleted,
                isActive && classes.circleActive,
              )}
            >
              {isCompleted ? (
                <CheckIcon className={classes.checkIcon} fontSize="inherit" />
              ) : null}
            </Box>
            {!isLast ? (
              <Box
                className={clsx(
                  classes.connector,
                  connectorCompleted && classes.connectorCompleted,
                )}
              />
            ) : null}
          </Box>
          <button
            type="button"
            className={clsx(
              classes.stepButton,
              isLast && classes.stepButtonLast,
            )}
            onClick={() => onSectionClick(section.id)}
            aria-current={isActive ? 'step' : undefined}
          >
            <Typography
              component="span"
              className={clsx(
                classes.stepLabel,
                isActive && classes.stepLabelActive,
              )}
            >
              {section.label}
            </Typography>
            <Typography component="span" className={classes.stepNumber}>
              Step {stepNumber}
            </Typography>
          </button>
        </Box>
      )
    })}
  </>
)

const CreateProfileSectionNav = ({
  sections,
  activeSection,
  completedSectionIds = new Set(),
  onSectionClick,
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const activeIndex = getActiveSectionIndex(sections, activeSection)
  const activeSectionConfig = sections[activeIndex]

  const handleSectionClick = sectionId => {
    onSectionClick(sectionId)
    setIsDrawerOpen(false)
  }

  return (
    <Box className={classes.root}>
      {isMobile ? (
        <Box className={classes.mobileNav}>
          <button
            type="button"
            className={classes.mobileSummary}
            onClick={() => setIsDrawerOpen(true)}
            aria-expanded={isDrawerOpen}
            aria-controls="create-profile-steps-drawer"
          >
            <Box className={classes.mobileSummaryText}>
              <Typography
                component="span"
                className={classes.mobileSummaryLabel}
              >
                {activeSectionConfig?.label}
              </Typography>
              <Typography
                component="span"
                className={classes.mobileSummaryStep}
              >
                Step {activeIndex + 1}
              </Typography>
            </Box>
            <UnfoldMoreIcon
              className={classes.mobileSummaryIcon}
              fontSize="small"
            />
          </button>
          <Drawer
            id="create-profile-steps-drawer"
            anchor="bottom"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            classes={{ paper: classes.drawerPaper }}
          >
            <Box className={classes.drawerHeader}>
              <Typography component="h2" className={classes.drawerTitle}>
                Steps
              </Typography>
              <IconButton
                type="button"
                className={classes.drawerCloseButton}
                onClick={() => setIsDrawerOpen(false)}
                aria-label="Close steps"
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box component="nav" aria-label="Create coach profile sections">
              <SectionStepList
                classes={classes}
                sections={sections}
                activeIndex={activeIndex}
                completedSectionIds={completedSectionIds}
                onSectionClick={handleSectionClick}
              />
            </Box>
          </Drawer>
        </Box>
      ) : null}

      <Box
        className={classes.desktopNav}
        component="nav"
        aria-label="Create coach profile sections"
      >
        <SectionStepList
          classes={classes}
          sections={sections}
          activeIndex={activeIndex}
          completedSectionIds={completedSectionIds}
          onSectionClick={onSectionClick}
        />
      </Box>
    </Box>
  )
}

export default CreateProfileSectionNav
